import type {BigCalendarEvent} from 'platform/big-calendar';

import {isNilOrEmpty} from 'ramda-adjunct';

import type {TestDriveResponseBody} from '@dms/api/sales';

import {parseDate} from 'shared/web';

import {getTestDriveCustomerName} from '../../utils/getTestDriveCustomerName';

export interface TestDriveEventData {
  userId: string;
  state: 'planned' | 'completed' | 'cancelled';
  note: string | null;
  internalNote: string | null;
  hasReturnedAt: boolean;
  scheduledAt: string;
  returnedAt: string;
}

export interface TestDriveCalendarEvent extends BigCalendarEvent {
  testDriveData: TestDriveEventData;
  businessCaseId: string | null;
  businessCaseCode: string | null;
  customerId: string;
  vehicleId: string;
}

export function transformTestDriveToCalendarEvent(
  testDrive: TestDriveResponseBody
): TestDriveCalendarEvent {
  const scheduledDate = parseDate(testDrive.scheduledAt);
  const endDate = testDrive.returnedAt ? parseDate(testDrive.returnedAt) : scheduledDate;

  const eventEndDate = testDrive.returnedAt
    ? endDate
    : parseDate(scheduledDate.getTime() + 60 * 60 * 1000); // eslint-disable-line eag/no-native-date-math -- Pre-existing native Date math, migrate to date-fns when possible

  const customerName = getTestDriveCustomerName(testDrive);

  return {
    id: testDrive.id,
    title: customerName,
    start: scheduledDate,
    end: eventEndDate,
    icon: isNilOrEmpty(testDrive.customerTradeName)
      ? 'sidebar/customers'
      : 'communication/business',
    color: 'palettes.blue.50.100',
    isReadOnly: false,
    businessCaseId: testDrive.businessCaseId,
    businessCaseCode: testDrive.businessCaseCode,
    customerId: testDrive.customerId,
    vehicleId: testDrive.vehicleId,
    alternative: testDrive.state === 'completed',

    testDriveData: {
      userId: testDrive.userId,
      state: testDrive.state,
      note: testDrive.note,
      scheduledAt: testDrive.scheduledAt,
      returnedAt: testDrive.returnedAt ?? eventEndDate.toISOString(),
      internalNote: testDrive.internalNote,
      hasReturnedAt: testDrive.returnedAt !== null,
    },
  };
}
